import { isEqual } from 'lodash-es'
import { DEFAULT_MODE, MODE_SPECS } from './modeSpecs'

/**
 * Pure transition logic of the selection store. Every mode/selection change flows through these
 * functions so the keep-vs-reset policy lives in exactly one place:
 *
 * - switching the mode keeps the selection iff the target mode can still hold it (`MODE_SPECS`),
 *   and keeps the sub-selection only if the selection survives and the target mode supports the
 *   sub-selection kind;
 * - selecting something a mode cannot hold switches to the selection's owning mode (`adopt`);
 * - changing the primary selection clears the sub-selection unless both are set atomically or
 *   the primary is unchanged.
 */

interface SelectionStateSnapshot {
  mode: EditorMode
  selection: SceneElementSelection | null
  subSelection: SceneElementSubSelection | null
  activeTab: string | null
  drawAreaLoadElement: string | null
}

interface SelectOptions {
  /** Sub-selection to set atomically with the primary (omit to clear on primary change). */
  sub?: SceneElementSubSelection | null
  /** Explicit target mode (skips adoption). */
  mode?: EditorMode
  /**
   * How the mode reacts to the new selection when no explicit `mode` is given. In every case the
   * result can hold the selection: when neither the current nor an owning mode fits, the mode
   * falls back to `'neutral'` rather than leaving an impossible mode/selection pairing.
   * - `'if-needed'` (default): keep the current mode when it can hold the selection, otherwise
   *   switch to the selection's owning mode;
   * - `'owning'`: always switch to the owning mode when one is derivable — StructuralPlanning's
   *   "the selection drives the drawer" behavior (clicking a beam opens the beam form even while
   *   a wall mode is active);
   * - `'keep'`: never adopt an owning mode — for pages whose panes are driven by their own tabs
   *   rather than by `EditorMode` (Results, IFC import), where the owning mode of a planar
   *   selection would be a Structural Planning mode and therefore wrong.
   */
  adopt?: 'if-needed' | 'owning' | 'keep'
  /** Active drawer tab; omitted = keep the current tab. */
  activeTab?: string | null
}

const isMultiSelection = (selection: SceneElementSelection): boolean =>
  selection.type === 'planar' && (!!selection.isMulti || selection.guids.length > 1)

/**
 * Whether the selection is *treated* as a multi-selection: an explicit/multi-guid planar
 * selection, or any planar selection held while the multi-edit mode is active (a multi-selection
 * reduced to one element stays in multi-edit). The editElementStore compat layer and the drawer
 * resolver both depend on this exact definition — it lives here so it cannot drift.
 */
const isMultiSelectionInMode = (
  selection: SceneElementSelection | null,
  mode: EditorMode,
): boolean => selection?.type === 'planar' && (isMultiSelection(selection) || mode === 'multi-edit')

/** Whether `mode` can hold `selection`. */
const isSelectableInMode = (mode: EditorMode, selection: SceneElementSelection): boolean => {
  const spec = MODE_SPECS[mode].selects
  if (!spec.kinds.includes(selection.type)) return false
  if (selection.type !== 'planar') return true

  if (isMultiSelection(selection) && !spec.multi) return false
  // An unresolved element type (mixed or unknown selection) only needs the kind to match.
  if (
    spec.planarTypes &&
    selection.elementType &&
    !spec.planarTypes.includes(selection.elementType)
  )
    return false

  return true
}

const isSubSelectableInMode = (mode: EditorMode, subSelection: SceneElementSubSelection): boolean =>
  MODE_SPECS[mode].subSelects.includes(subSelection.type)

/**
 * The mode a selection natively belongs to (used for adoption), `null` when the current mode
 * should simply be kept (kind without a dedicated mode, or unresolved element type).
 */
const owningModeOf = (selection: SceneElementSelection): EditorMode | null => {
  switch (selection.type) {
    case 'planar': {
      if (isMultiSelection(selection)) return 'multi-edit'
      if (!selection.elementType) return null
      return selection.elementType === 'opening' ? 'openings' : selection.elementType
    }
    default:
      return null
  }
}

const isSamePrimarySelection = (
  a: SceneElementSelection | null,
  b: SceneElementSelection | null,
): boolean => isEqual(a, b)

/** `drawAreaLoadElement` only exists while the area-load mode is active. */
const reconcileDrawAreaLoadElement = (
  state: SelectionStateSnapshot,
  nextMode: EditorMode,
): string | null => (nextMode === 'area-load' ? state.drawAreaLoadElement : null)

/** Switch the mode, keeping whatever the target mode can still hold. */
const reconcileModeSwitch = (
  state: SelectionStateSnapshot,
  nextMode: EditorMode,
): SelectionStateSnapshot => {
  if (nextMode === state.mode) return state

  const keepSelection = state.selection !== null && isSelectableInMode(nextMode, state.selection)
  const keepSubSelection =
    keepSelection &&
    state.subSelection !== null &&
    isSubSelectableInMode(nextMode, state.subSelection)

  return {
    mode: nextMode,
    selection: keepSelection ? state.selection : null,
    subSelection: keepSubSelection ? state.subSelection : null,
    activeTab: keepSelection ? state.activeTab : null,
    drawAreaLoadElement: reconcileDrawAreaLoadElement(state, nextMode),
  }
}

/** Set the primary selection (and optionally sub-selection/mode/tab) atomically. */
const reconcileSelect = (
  state: SelectionStateSnapshot,
  selection: SceneElementSelection | null,
  options: SelectOptions = {},
): SelectionStateSnapshot => {
  if (selection === null) {
    const mode = options.mode ?? state.mode
    // A sub-selection is scoped to a primary selection (invariant #3 in the README) — without one
    // there is nothing for it to scope to, so it is always discarded here regardless of `options.sub`.
    return {
      mode,
      selection: null,
      subSelection: null,
      activeTab: options.activeTab !== undefined ? options.activeTab : state.activeTab,
      drawAreaLoadElement: reconcileDrawAreaLoadElement(state, mode),
    }
  }

  let mode = options.mode ?? state.mode
  if (options.mode === undefined) {
    const adopt = options.adopt ?? 'if-needed'
    const currentModeFits = isSelectableInMode(state.mode, selection)
    // Whatever we end up with must be able to hold the selection, hence the neutral fallback:
    // keeping a mode that cannot is what leaves the drawer and the scene disagreeing.
    const fallbackMode = currentModeFits ? state.mode : DEFAULT_MODE

    if (adopt === 'keep') mode = fallbackMode
    else if (adopt === 'owning' || !currentModeFits) mode = owningModeOf(selection) ?? fallbackMode
  } else if (!isSelectableInMode(mode, selection)) {
    // An explicit mode is not exempt from the guarantee above — a caller passing one that cannot
    // hold the selection falls back the same way the adoption paths do.
    mode = owningModeOf(selection) ?? DEFAULT_MODE
  }

  const samePrimary = isSamePrimarySelection(selection, state.selection)
  const subSelection =
    options.sub !== undefined ? options.sub : samePrimary ? state.subSelection : null

  return {
    mode,
    selection,
    subSelection:
      subSelection !== null && isSubSelectableInMode(mode, subSelection) ? subSelection : null,
    activeTab: options.activeTab !== undefined ? options.activeTab : state.activeTab,
    drawAreaLoadElement: reconcileDrawAreaLoadElement(state, mode),
  }
}

/** Entering a page resets a foreign page-bound mode to neutral (selection reconciled with it). */
const reconcilePageEnter = (
  state: SelectionStateSnapshot,
  page: EditorPage,
): SelectionStateSnapshot => {
  const modePage = MODE_SPECS[state.mode].page
  if (modePage === '*' || modePage === page) return state

  return reconcileModeSwitch(state, DEFAULT_MODE)
}

const paneTarget = (selection: SceneElementSelection | null) => {
  if (!selection) return null
  if ('locations' in selection) {
    return {
      type: selection.type,
      guids: selection.locations.map(({ guid, position }) => `${guid}:${position}`),
    }
  }
  return 'guids' in selection
    ? { type: selection.type, guids: selection.guids }
    : { type: selection.type, guid: selection.guid }
}

/**
 * Whether a transition replaces the drawer pane: another element, another mode or another tab.
 * Changing only the sub-selection (e.g. picking another connector of the same wall) keeps the pane.
 */
const changesSelectionPane = (
  current: SelectionStateSnapshot,
  next: SelectionStateSnapshot,
): boolean =>
  current.mode !== next.mode ||
  current.activeTab !== next.activeTab ||
  !isEqual(paneTarget(current.selection), paneTarget(next.selection))

export {
  changesSelectionPane,
  isMultiSelection,
  isMultiSelectionInMode,
  isSelectableInMode,
  isSubSelectableInMode,
  isSamePrimarySelection,
  owningModeOf,
  reconcileModeSwitch,
  reconcileSelect,
  reconcilePageEnter,
}
export type { SelectionStateSnapshot, SelectOptions }
